import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';

import {cn} from '../lib/utils';
import {cva, VariantProps} from 'class-variance-authority';

const progressVariants = cva('flex gap-2 items-center justify-center', {
    variants: {
        variant: {
            primary: 'bg-progressBar-background relative h-[0.5rem] w-full rounded-full',
            secondary: 'bg-progressBar-background relative h-[0.5rem] w-full rounded-full',
            warning: 'bg-progressBar-background relative h-[0.5rem] w-full rounded-full',
            overflow: 'bg-progressBar-background relative h-[0.5rem] w-full rounded-full',
            small: 'bg-[#B3B8DB] relative h-[5px] w-full overflow-hidden rounded-full',
        },
        indicator: {
            primary: 'bg-[#F2F4F7]',
            secondary: 'bg-[#333333]',
            warning: 'bg-[#FFB800]',
            overflow: 'bg-[#FF4D4F]',
            uploading: 'bg-[#B3B8DB]',
            uploaded: 'bg-[#17B26A]',
            error: '',
            none: '',
        },
    },
    defaultVariants: {
        variant: 'primary',
        indicator: 'primary',
    },
});

interface ProgressBarProps
    extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
        VariantProps<typeof progressVariants> {
    position?: 'right' | 'bottom' | 'top-floating' | 'bottom-floating';
    value?: number;
}

const Progressbar = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressBarProps>(
    ({className, variant, indicator, value, position, ...props}, ref) => {
        const showValueOnRight = position === 'right';

        return (
            <div style={{display: 'flex', alignItems: 'center'}} className='w-full'>
                <ProgressPrimitive.Root
                    ref={ref}
                    className={cn(
                        'relative h-[0.5rem] w-full overflow-hidden rounded-full bg-progressBar-background',
                        className,
                    )}
                    {...props}>
                    <ProgressPrimitive.Indicator
                        className={cn(
                            progressVariants({
                                indicator: indicator,
                            }),
                            'h-full w-full flex-1 rounded-progressIndicator transition-all',
                        )}
                        style={{transform: `translateX(-${100 - (value || 0)}%)`}}
                    />
                </ProgressPrimitive.Root>
                {showValueOnRight && (
                    <div
                        className='h-[1.25rem] pl-[0.75rem]
                text-center font-[31.25rem] text-progressBar-textColor'>
                        {value}%
                    </div>
                )}
            </div>
        );
    },
);

Progressbar.displayName = ProgressPrimitive.Root.displayName;

export {Progressbar};
